import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* ────────────────────────────── COMPONENTS ────────────────────────────── */

function Navbar() {
  const [stuck, setStuck] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const fn = () => setStuck(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const handleNavClick = (path) => {
    navigate(path);
  };

  return (
    <nav className={`vz-nav${stuck ? " stuck" : ""}`}>
      <div className="vz-nav-inner">
        <div className="vz-logo">
          Viz<em>infy</em>
        </div>
        <div className="vz-links">
          {[
            { label: "Home", path: "/" },
            { label: "Services", path: "/services" },
            { label: "Portfolio", path: "/portfolio" },
            { label: "About Us", path: "/about" },
            { label: "Blog", path: "/blog" }
          ].map(({ label, path }) => (
            <button key={label} className="vz-link" onClick={() => handleNavClick(path)}>
              {label}
            </button>
          ))}
        </div>
        <button className="vz-cta">Contact Us</button>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-inner">
        <div>
          <div className="htag hero-anim-tag">
            <span className="htag-dot" />
            IT Solutions &amp; Digital Innovation
          </div>
          <h1 className="hero-h1 hero-anim-h1">
            Powerful IT
            <br />
            <span className="o">Solutions</span>
            <br />
            for your
            <br />
            Business
          </h1>
          <p
            className="hero-p hero-anim-sub"
            style={{ transitionDelay: "0.05s" }}
          >
            Let's scale, shape &amp; shine — deploying game-changing experiences
            that will take your brand from bland to buzz-worthy.
          </p>
          <div className="hero-btns hero-anim-btns">
            <button className="btn-fill">
              Get Started
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            <button className="btn-line">
              <span className="play-circ">▶</span>
              Watch Demo
            </button>
          </div>
          <div className="hero-stats hero-anim-stats">
            {[
              ["50+", "Years Experience"],
              ["200+", "Projects Delivered"],
              ["98%", "Client Satisfaction"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="hs-num">{n}</div>
                <div className="hs-lbl">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-vis hero-anim-vis">
          <div className="hero-vis-shell">
            <div className="vis-circle" />
            <div className="blob blob-v" />
            <div className="blob blob-y" />
            <div className="blob blob-g" />
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=480&q=80"
              alt="Team"
              className="vis-img"
            />
            <div className="fc fc1">
              <div className="fc-ico">🚀</div>
              <div>
                <div className="fc-title">Project Launched</div>
                <div className="fc-sub">E-commerce Platform</div>
              </div>
            </div>
            <div className="fc fc2">
              <div className="av-row">
                {["#f59e0b", "#3b82f6", "#10b981"].map((c) => (
                  <div key={c} className="av" style={{ background: c }} />
                ))}
              </div>
              <div style={{ marginLeft: 10 }}>
                <div className="fc-title">50+ Happy Clients</div>
                <div className="fc-sub" style={{ color: "#f59e0b" }}>
                  ★★★★★
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Clients() {
  const names = [
    "Mercedes-Benz",
    "Nestlé",
    "ISB",
    "DLF",
    "Thomas Cook",
    "Nissan",
    "Domino's",
    "Jamboree",
    "DDB",
  ];
  return (
    <div className="clients-band">
      <p className="t-label">Trusted by industry leaders</p>
      <div className="t-mask">
        <div className="t-track">
          {[...names, ...names, ...names].map((n, i) => (
            <div key={i} className="t-item">
              {n}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WhyChooseUs() {
  const items = [
    {
      t: "Strike Team",
      d: "Agile squads that move fast and ship quality without cutting corners. We keep scope tight and velocity high.",
    },
    {
      t: "Build End-to-End",
      d: "From ideation and architecture through to deployment and maintenance — we handle every layer of your product.",
    },
    {
      t: "Client Outcomes",
      d: "Every technical decision is benchmarked against your business results, not engineering vanity metrics.",
    },
    {
      t: "Best Practice for Business",
      d: "Industry standards combined with proprietary methodologies refined over 50 years of collective experience.",
    },
    {
      t: "Cloud Operations",
      d: "Scalable, secure, reliable infrastructure backed by modern DevOps practices and proactive monitoring.",
    },
    {
      t: "Fully Agile Growth",
      d: "Continuous iteration cycles with real feedback loops ensure your product improves with every sprint.",
    },
  ];
  return (
    <div className="why-sec">
      <div className="why-inner">
        <div className="why-sticky">
          <span
            data-reveal="fade-right"
            data-duration="normal"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#ff5000",
              display: "block",
              marginBottom: 14,
            }}
          >
            Why Choose Us
          </span>
          <h2 data-reveal="fade-right" data-duration="slow" className="why-h2">
            Working with <em>50 years</em> of collective experience
          </h2>
          <p data-reveal="fade-right" data-delay="100" className="why-p">
            By partnering with us, you get an evidence-backed growth culture,
            versatile partnerships, and result-based methodologies backed by
            genuine passion.
          </p>
          <button data-reveal="fade-up" data-delay="200" className="btn-fill">
            Start a Project
          </button>
        </div>
        <div data-stagger className="why-items">
          {items.map((item, i) => (
            <div key={item.t} className="why-row">
              <div className="why-n">0{i + 1}</div>
              <div>
                <div className="why-t">{item.t}</div>
                <div className="why-d">{item.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Technology() {
  const [active, setActive] = useState(0);

  const tabs = [
    {
      label: "AR / VR",
      img: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=1200&q=80",
      chips: [
        "Augmented Reality",
        "Virtual Reality",
        "WebXR",
        "Oculus / Meta",
        "Spatial Computing",
      ],
    },
    {
      label: "Web App",
      img: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&q=80",
      chips: [
        "React / Next.js",
        "Node.js",
        "Cloud-Hosted",
        "REST & GraphQL",
        "CI/CD Pipeline",
      ],
    },
    {
      label: "SaaS",
      img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80",
      chips: [
        "Multi-Tenant",
        "Subscription Billing",
        "Analytics Dashboard",
        "Role-Based Access",
        "API-First",
      ],
    },
    {
      label: "SEO & Marketing",
      img: "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=1200&q=80",
      chips: [
        "Technical SEO",
        "Content Strategy",
        "PPC Campaigns",
        "Email Marketing",
        "Conversion Optimisation",
      ],
    },
  ];

  const current = tabs[active];

  return (
    <div className="tech-sec">
      <div className="tech-in">
        <div className="tech-hd">
          <span data-reveal="fade-down" className="tech-ey">
            What We Build
          </span>
          <h2 data-reveal="fade-up" data-duration="slow" className="tech-h2">
            We Build any Kind of <span>Technology</span>
          </h2>
        </div>

        {/* Tab bar — NO data-stagger here, animate the whole bar as one unit */}
        <div data-reveal="fade-up" data-delay="150" className="tech-tabs">
          {tabs.map((t, i) => (
            <button
              key={t.label}
              className={`tt${active === i ? " on" : ""}`}
              onClick={() => setActive(i)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          data-reveal="fade-scale"
          data-duration="slow"
          data-delay="200"
          className="tech-shell"
        >
          <div className="tech-bar">
            <span className="md" style={{ background: "#FF5F56" }} />
            <span className="md" style={{ background: "#FFBD2E" }} />
            <span className="md" style={{ background: "#27C93F" }} />
            <span
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.28)",
                marginLeft: 8,
              }}
            >
              {current.label} — Advanced Technology Platform
            </span>
          </div>
          <img
            key={current.img}
            src={current.img}
            alt={current.label}
            className="tech-ph"
            style={{ animation: "techImgFade 0.4s ease" }}
          />
          <div className="tech-chips">
            {current.chips.map((c) => (
              <div key={c} className="tch">
                <span className="chk">✓</span>
                {c}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Services() {
  const groups = [
    {
      label: "Immersive Technology",
      services: [
        {
          ico: "🥽",
          name: "Augmented Reality",
          tag: "AR",
          desc: "Mobile AR, WebAR, and marker-based experiences for retail, training, and brand activations.",
        },
        {
          ico: "🌐",
          name: "Virtual Reality",
          tag: "VR",
          desc: "Custom VR environments for training simulations, virtual showrooms, and immersive campaigns.",
        },
        {
          ico: "🌀",
          name: "Mixed Reality",
          tag: "MR",
          desc: "HoloLens & Magic Leap solutions for enterprise workflows, product demos, and spatial computing.",
        },
        {
          ico: "🧪",
          name: "Virtual Try On",
          tag: "AI",
          desc: "AI-powered fitting rooms that reduce returns and lift conversions across fashion and beauty.",
        },
        {
          ico: "📸",
          name: "Holograms",
          tag: "Display",
          desc: "Holographic display solutions for events, retail flagships, and brand activations.",
        },
      ],
    },
    {
      label: "Digital Marketing",
      services: [
        {
          ico: "📣",
          name: "Social Media Marketing",
          tag: "Social",
          desc: "Strategy-led campaigns across Instagram, LinkedIn, and TikTok that turn followers into customers.",
        },
        {
          ico: "📊",
          name: "Digital Marketing",
          tag: "Growth",
          desc: "Full-funnel strategy — PPC, SEO, email, and content — measured against revenue, not vanity metrics.",
        },
        {
          ico: "🤖",
          name: "Chat & Voice Bots",
          tag: "AI",
          desc: "GPT-powered bots that qualify leads and resolve queries 24/7 across web, WhatsApp, and IVR.",
        },
      ],
    },
    {
      label: "Software Development",
      services: [
        {
          ico: "📱",
          name: "Mobile App Development",
          tag: "Mobile",
          desc: "Native iOS & Android and cross-platform apps — from MVPs to products used by millions daily.",
        },
        {
          ico: "💻",
          name: "Web Applications",
          tag: "Web",
          desc: "SaaS platforms, marketplaces, dashboards, and enterprise portals built for production scale.",
        },
        {
          ico: "🎮",
          name: "Game Development",
          tag: "Games",
          desc: "End-to-end game design for mobile, PC, and immersive platforms — concept through live-ops.",
        },
        {
          ico: "⛓️",
          name: "Blockchain & NFT",
          tag: "Web3",
          desc: "Smart contracts, dApps, and NFT platforms with audited, gas-optimised architecture.",
        },
      ],
    },
  ];

  return (
    <div className="srv-sec">
      <div className="srv-in">
        <div className="srv-layout">
          {/* Left sticky panel */}
          <div className="srv-left">
            <span data-reveal="fade-right" className="srv-eyebrow">
              What We Do
            </span>
            <h2
              data-reveal="fade-right"
              data-duration="slow"
              className="srv-h2"
            >
              Services We Provide
            </h2>
            <p
              data-reveal="fade-right"
              data-delay="100"
              className="srv-left-desc"
            >
              A full-spectrum technology partner — from immersive experiences to
              enterprise software — for businesses that want to move faster and
              build smarter.
            </p>
            <div data-reveal="fade-up" data-delay="200" className="srv-count">
              <div className="srv-count-num">12</div>
              <div className="srv-count-lbl">
                services across 3 practice areas
              </div>
            </div>
          </div>

          {/* Right: grouped rows */}
          <div className="srv-right">
            {groups.map((g) => (
              <div key={g.label} className="srv-group">
                <div data-reveal="fade-in" className="srv-group-label">
                  {g.label}
                </div>
                <div data-stagger>
                  {g.services.map((s) => (
                    <div key={s.name} className="srv-row">
                      <div className="srv-row-ico">{s.ico}</div>
                      <div className="srv-row-body">
                        <div className="srv-row-name">{s.name}</div>
                        <div className="srv-row-desc">{s.desc}</div>
                      </div>
                      <div className="srv-row-tag">{s.tag}</div>
                      <div className="srv-row-arrow">→</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Blog() {
  const posts = [
    {
      tag: "Branding",
      title: "Personalisation Trends in Email Marketing: Boosting Engagement",
      exc: "Personalised emails improve open rates and build real loyalty by speaking to each subscriber as an individual.",
      img: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=500&q=80",
      date: "Oct 14, 2024",
    },
    {
      tag: "Mixed Reality",
      title:
        "Virtual Reality to Spur Broadcasting: Enhancing the Fan Experience",
      exc: "Mixed reality in sports broadcasting is redefining how fans consume live events from anywhere on Earth.",
      img: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?w=500&q=80",
      date: "Sep 28, 2024",
    },
    {
      tag: "SEO",
      title:
        "The Future of SEO: Navigating Algorithm Updates and Staying Ahead",
      exc: "Google's algorithm shifts demand a rethink of content strategy, authority, and technical hygiene.",
      img: "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=500&q=80",
      date: "Sep 10, 2024",
    },
  ];
  return (
    <div className="blog-sec">
      <div className="blog-in">
        <div className="blog-hd">
          <div>
            <span
              data-reveal="fade-up"
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#ff5000",
                display: "block",
                marginBottom: 12,
              }}
            >
              Blog
            </span>
            <h2
              data-reveal="fade-up"
              data-delay="100"
              style={{
                fontFamily: "Epilogue,sans-serif",
                fontSize: "clamp(26px,3vw,44px)",
                fontWeight: 900,
                letterSpacing: "-1.5px",
                color: "#111",
                lineHeight: 1.1,
              }}
            >
              Our Latest{" "}
              <span style={{ color: "#ff5000" }}>News & Updates</span>
            </h2>
          </div>
          <button
            data-reveal="fade-left"
            className="btn-line"
            style={{ fontSize: 14, padding: "10px 22px" }}
          >
            View All Articles
          </button>
        </div>
        <div data-stagger className="blog-grid">
          {posts.map((p) => (
            <article key={p.title} className="bc">
              <div className="bc-img-w">
                <img src={p.img} alt={p.title} className="bc-img" />
                <span className="bc-pill">{p.tag}</span>
              </div>
              <div className="bc-body">
                <p className="bc-date">{p.date}</p>
                <h5 className="bc-title">{p.title}</h5>
                <p className="bc-exc">{p.exc}</p>
                <a href="#" className="bc-more">
                  Read More{" "}
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function CTA() {
  return (
    <div className="cta-sec">
      <div className="cta-in">
        <div className="cta-grid">
          <div>
            <h2
              data-reveal="fade-right"
              data-duration="slow"
              className="cta-h2"
            >
              Start Your Project With Us
            </h2>
            <p data-reveal="fade-right" data-delay="100" className="cta-p">
              Our team is ready to turn your boldest ideas into working
              technology. Tell us what you're building — let's make it real.
            </p>
            <div data-stagger className="cta-list">
              {[
                "Free initial consultation",
                "NDA available on request",
                "Dedicated project manager",
                "Agile delivery sprints",
              ].map((c) => (
                <div key={c} className="cta-row">
                  <div className="cta-ico">✓</div>
                  <div className="cta-txt">{c}</div>
                </div>
              ))}
            </div>
          </div>
          <div
            data-reveal="fade-left"
            data-duration="slow"
            data-delay="150"
            className="cta-form"
          >
            <h4>Drop Us a Message</h4>
            <div className="ff">
              <input className="vi" placeholder="Your Name" />
            </div>
            <div className="ff">
              <input className="vi" type="email" placeholder="Email Address" />
            </div>
            <div className="ff">
              <select className="vi">
                <option value="">Select a Service</option>
                <option>Web Application</option>
                <option>Mobile App</option>
                <option>AR / VR</option>
                <option>Digital Marketing</option>
                <option>Blockchain</option>
              </select>
            </div>
            <div className="ff">
              <textarea
                className="vi"
                rows={3}
                placeholder="Tell us about your project..."
              />
            </div>
            <button className="btn-sub">Send Message →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  const cols = [
    { h: "Company", l: ["Home", "About Us", "Portfolio", "Careers", "Blog"] },
    {
      h: "Services",
      l: [
        "Augmented Reality",
        "Social Media",
        "Game Dev",
        "Blockchain",
        "Mobile Apps",
      ],
    },
    {
      h: "Solutions",
      l: [
        "Virtual Reality",
        "Digital Marketing",
        "Web Applications",
        "Mixed Reality",
        "Holograms",
      ],
    },
  ];
  return (
    <footer className="ftr">
      <div className="ftr-in">
        <div data-stagger className="ftr-grid">
          <div>
            <div className="ftr-logo">
              Viz<em>infy</em>
            </div>
            <p className="ftr-tag">
              Building the technology that powers tomorrow's most ambitious
              businesses.
            </p>
            <div className="ftr-socs">
              {["𝕏", "in", "f", "▶"].map((s, i) => (
                <button key={i} className="fsoc">
                  {s}
                </button>
              ))}
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <p className="ftr-col-h">{c.h}</p>
              <div className="ftr-links">
                {c.l.map((l) => (
                  <a key={l} href="#">
                    {l}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div data-reveal="fade-up" data-delay="200" className="ftr-bottom">
          <span>© 2024 Vizinfy. All rights reserved.</span>
          <div className="gap-20">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ── BIDIRECTIONAL SCROLL ANIMATION ENGINE ── */
function useScrollReveal() {
  useEffect(() => {
    // Direction tracking — updated on every scroll tick
    let scrollDir = "down";
    let lastY = window.scrollY;
    const updateDir = () => {
      const y = window.scrollY;
      if (y !== lastY) {
        scrollDir = y > lastY ? "down" : "up";
        lastY = y;
      }
    };
    window.addEventListener("scroll", updateDir, { passive: true });

    // ── Prepare all elements ──────────────────────────────────
    // Singles: direct [data-reveal] elements (not inside [data-stagger])
    const singles = Array.from(
      document.querySelectorAll(
        "[data-reveal]:not([data-stagger] [data-reveal])",
      ),
    );

    // Stagger groups: tag each child with fade-up + stagger delay
    // Skip any stagger parent that contains interactive buttons (those handle their own state)
    const staggerParents = Array.from(
      document.querySelectorAll("[data-stagger]"),
    );
    const staggerChildren = [];
    staggerParents.forEach((parent) => {
      // Don't stagger tab bars or any container whose children are buttons with onClick
      if (parent.querySelector("button.tt") || parent.dataset.skipStagger)
        return;
      Array.from(parent.children).forEach((child, i) => {
        child.setAttribute("data-reveal", "fade-up");
        child.style.transitionDelay = i * 85 + "ms";
        child.style.transitionDuration = "650ms";
        staggerChildren.push(child);
      });
    });

    // All observed elements
    const allEls = [...singles, ...staggerChildren];

    // Apply duration/delay overrides from data-attributes on singles
    singles.forEach((el) => {
      const dur = el.dataset.duration;
      const delay = el.dataset.delay;
      if (dur === "slow") el.style.transitionDuration = "900ms";
      else if (dur === "fast") el.style.transitionDuration = "380ms";
      else el.style.transitionDuration = "700ms";
      if (delay) el.style.transitionDelay = delay + "ms";
    });

    // ── Observer ─────────────────────────────────────────────
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const rect = entry.boundingClientRect;
          const vh = window.innerHeight;

          if (entry.isIntersecting) {
            // Entering viewport → animate IN
            el.classList.remove("rv-out");
            // Restore original timing when animating in
            const dur = el.dataset.duration;
            if (!el.dataset.staggerChild) {
              if (dur === "slow") el.style.transitionDuration = "900ms";
              else if (dur === "fast") el.style.transitionDuration = "380ms";
              else el.style.transitionDuration = "700ms";
            } else {
              el.style.transitionDuration = "650ms";
            }
            el.style.transitionTimingFunction =
              "cubic-bezier(0.22, 1, 0.36, 1)";
            requestAnimationFrame(() =>
              requestAnimationFrame(() => el.classList.add("rv-in")),
            );
          } else {
            // Left viewport — only reverse-animate if scrolling BACK UP
            // (element is now BELOW the viewport = it hasn't been seen yet, don't touch it)
            // element is ABOVE viewport = user scrolled back up past it → hide it again
            const isAboveViewport = rect.bottom < 0;
            const isBelowViewport = rect.top > vh;

            if (scrollDir === "up" && isBelowViewport) {
              // Scrolled up and element is below — reset it so it can animate in again
              el.classList.remove("rv-in");
              el.classList.add("rv-out");
              el.style.transitionDuration = "260ms";
              el.style.transitionTimingFunction = "cubic-bezier(0.4, 0, 1, 1)";
            }
            // Note: we intentionally do NOT hide elements that scroll above the viewport
            // (that would be disorienting — content disappearing as you read)
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -60px 0px",
      },
    );

    allEls.forEach((el) => {
      if (el.dataset.staggerChild === undefined)
        el.dataset.staggerChild = "true";
      observer.observe(el);
    });
    // Singles don't have staggerChild
    singles.forEach((el) => delete el.dataset.staggerChild);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateDir);
    };
  }, []);
}

/* ── ROOT ── */
export default function Home() {
  useScrollReveal();

  return (
    <>
      <Hero />
      <Clients />
      <WhyChooseUs />
      <Technology />
      <Services />
      <Blog />
      <CTA />
    </>
  );
}

export { Navbar };
